/**
 * Session Code Generator
 *
 * Generates unguessable session codes for phone ↔ reader pairing.
 * Uses cryptographically secure random bytes encoded as base32.
 *
 * Codes are 10-12 characters, URL-safe, and easy to read/type.
 */

/**
 * Base32 alphabet (Crockford variant - avoids ambiguous chars like I/L/O)
 * Lowercase for readability on mobile
 */
const BASE32_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

/**
 * Default code length (10-12 chars provides ~50-60 bits of entropy)
 */
const DEFAULT_CODE_LENGTH = 10;

/**
 * Generate a cryptographically secure session code.
 *
 * @param length - Code length (default: 10, max: 32)
 * @returns Unguessable base32-encoded session code
 */
export function generateSessionCode(length: number = DEFAULT_CODE_LENGTH): string {
  const clampedLength = Math.min(32, Math.max(8, length));
  const bytes = new Uint8Array(clampedLength);

  // Use crypto.getRandomValues for cryptographic randomness
  crypto.getRandomValues(bytes);

  let code = '';
  for (let i = 0; i < clampedLength; i++) {
    // Map each byte to a base32 character (5 bits used, 32 possible values)
    code += BASE32_ALPHABET[bytes[i] % 32];
  }

  return code;
}

/**
 * Validate a session code format.
 *
 * @param code - Code to validate
 * @returns true if code is valid format
 */
export function isValidSessionCode(code: string): boolean {
  if (!code || typeof code !== 'string') {
    return false;
  }

  // Must be 8-32 characters
  if (code.length < 8 || code.length > 32) {
    return false;
  }

  // Must only contain base32 alphabet characters
  for (const char of code) {
    if (!BASE32_ALPHABET.includes(char)) {
      return false;
    }
  }

  return true;
}

/**
 * Get the Supabase channel name for a session code.
 *
 * @param sessionCode - The session code
 * @returns Channel name in format "pair:<code>"
 */
export function getChannelName(sessionCode: string): string {
  return `pair:${sessionCode}`;
}

/**
 * Extract session code from URL query parameter.
 *
 * @param url - URL or search params string
 * @returns Session code if found and valid, null otherwise
 */
export function extractSessionCodeFromUrl(url: string): string | null {
  try {
    const searchParams = new URLSearchParams(
      url.includes('?') ? url.split('?')[1] : url
    );
    const code = searchParams.get('session');

    if (code && isValidSessionCode(code)) {
      return code;
    }
  } catch {
    // Invalid URL format
  }

  return null;
}

/**
 * Build phone companion URL with session code.
 *
 * @param baseUrl - Base URL (e.g., window.location.origin)
 * @param sessionCode - Session code to include
 * @returns Full phone URL with session parameter
 */
export function buildPhoneUrl(baseUrl: string, sessionCode: string): string {
  const url = new URL('/phone', baseUrl);
  url.searchParams.set('session', sessionCode);
  return url.toString();
}
